/**
 * CreateAudiencesDialog Component
 *
 * Modal dialog for creating audiences from selected tasks.
 * Opens when clicking "Create Audiences" button, shows task selection
 * with all tasks selected by default, and Audience Count input.
 *
 * Features:
 * - Task list with multi-select checkboxes
 * - All tasks selected by default when dialog opens
 * - Audience Count input field
 * - Create Audiences button in footer
 * - Loading state during submission
 *
 * @module product
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Stack,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  FormControlLabel,
  Switch,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { SDialog } from '@modules/ui-components/dialogs/SDialog';
import {
  AvailableModelSelector,
  PROJECT_DEFAULT_AI_MODEL,
  useProjectDefaultAIModel,
} from '@modules/ai-settings';
import { AUDIENCE_GENERATION } from '@test-kit/selectors/audiences';
import type { Task } from '@segmently/schema-models/entities';

// ============================================================================
// CONSTANTS
// ============================================================================

const AUDIENCE_COUNT_DEFAULT = 4;
const AUDIENCE_COUNT_MIN = 1;
const AUDIENCE_COUNT_MAX = 20;

// ============================================================================
// TYPES
// ============================================================================

export interface CreateAudiencesDialogProps {
  /** Whether dialog is open */
  open: boolean;
  /** Available tasks to select from */
  tasks: Task[];
  /** Default audience count */
  defaultAudienceCount?: number;
  /** Project ID used to resolve project-connected AI models */
  projectId?: string;
  /**
   * Project audience model. 'b2b' switches dialog copy + count semantics:
   * audiences become ICP archetypes whose actors form a DMU stakeholder pack.
   * Defaults to 'b2c'.
   */
  audienceModel?: 'b2c' | 'b2b';
  /** Callback when dialog is closed */
  onClose: () => void;
  /** Callback when audiences should be created */
  onCreateAudiences: (
    audienceCount: number,
    selectedTaskIds: string[],
    useDeepResearch: boolean,
    model?: string,
  ) => Promise<void>;
}

// ============================================================================
// COMPONENT
// ============================================================================

/**
 * CreateAudiencesDialog Component
 *
 * Dialog for selecting tasks and creating audiences.
 * All tasks are selected by default when the dialog opens.
 */
export const CreateAudiencesDialog: React.FC<CreateAudiencesDialogProps> = ({
  open,
  tasks,
  defaultAudienceCount = AUDIENCE_COUNT_DEFAULT,
  projectId,
  audienceModel = 'b2c',
  onClose,
  onCreateAudiences,
}) => {
  const theme = useTheme();
  const projectDefaultModel = useProjectDefaultAIModel(projectId);

  // ============================================================================
  // STATE
  // ============================================================================

  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [audienceCount, setAudienceCount] = useState(defaultAudienceCount);
  const [useDeepResearch, setUseDeepResearch] = useState(false);
  const [selectedModel, setSelectedModel] = useState<string>(PROJECT_DEFAULT_AI_MODEL);
  const [modelTouched, setModelTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  // Select all tasks by default when dialog opens
  useEffect(() => {
    if (open) {
      const allTaskIds = tasks.map(t => t.id || '').filter(Boolean);
      setSelectedTaskIds(allTaskIds);
      setAudienceCount(defaultAudienceCount);
      setUseDeepResearch(false);
      setModelTouched(false);
    }
  }, [open, tasks, defaultAudienceCount]);

  useEffect(() => {
    if (!open || modelTouched) return;
    setSelectedModel(projectDefaultModel.value);
  }, [modelTouched, open, projectDefaultModel.value]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleToggleTask = useCallback((taskId: string) => {
    setSelectedTaskIds(prev =>
      prev.includes(taskId)
        ? prev.filter(id => id !== taskId)
        : [...prev, taskId]
    );
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedTaskIds.length === tasks.length) {
      setSelectedTaskIds([]);
    } else {
      setSelectedTaskIds(tasks.map(t => t.id || '').filter(Boolean));
    }
  }, [tasks, selectedTaskIds.length]);

  const handleModelChange = useCallback((model: string) => {
    setModelTouched(true);
    setSelectedModel(model);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (selectedTaskIds.length === 0) return;

    setIsSubmitting(true);
    try {
      await onCreateAudiences(audienceCount, selectedTaskIds, useDeepResearch, selectedModel);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  }, [audienceCount, selectedTaskIds, selectedModel, useDeepResearch, onCreateAudiences, onClose]);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  const primaryAction = useMemo(() => ({
    label: isSubmitting
      ? 'Creating...'
      : audienceModel === 'b2b' ? 'Create ICP Archetypes' : 'Create Audiences',
    onClick: handleSubmit,
    disabled: isSubmitting || selectedTaskIds.length === 0,
    loading: isSubmitting,
    'data-testid': AUDIENCE_GENERATION.submitButton,
  }), [isSubmitting, handleSubmit, selectedTaskIds.length, audienceModel]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <SDialog
      open={open}
      onClose={onClose}
      title={audienceModel === 'b2b' ? 'Create ICP Archetypes (B2B)' : 'Create Audiences'}
      maxWidth="md"
      fullWidth
      primaryAction={primaryAction}
      data-testid={AUDIENCE_GENERATION.dialog}
    >
      <Stack spacing={3}>
        <Typography variant="body2" color="text.secondary">
          {audienceModel === 'b2b'
            ? 'Each ICP (Ideal Customer Profile) archetype describes a typical company that buys/uses this product to perform a selected task. Its actors will be a Decision-Making Unit (DMU) pack of stakeholder roles. All tasks are selected by default.'
            : 'Select tasks to use for audience generation. All tasks are selected by default.'}
        </Typography>

        <AvailableModelSelector
          projectId={projectId}
          value={selectedModel}
          onChange={handleModelChange}
          label="AI Model"
          size="small"
          fullWidth
          disabled={isSubmitting}
          testId={AUDIENCE_GENERATION.modelSelector}
        />

        {/* Audience Count */}
        <TextField
          label={audienceModel === 'b2b' ? 'ICP Archetype Count' : 'Audience Count'}
          type="number"
          value={audienceCount}
          onChange={(e) => setAudienceCount(Number(e.target.value))}
          slotProps={{
            htmlInput: {
              min: AUDIENCE_COUNT_MIN,
              max: AUDIENCE_COUNT_MAX,
              'data-testid': AUDIENCE_GENERATION.countInput,
            },
          }}
          size="small"
          sx={{ width: theme.spacing(19) }}
        />

        {/* Deep Research Toggle */}
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={useDeepResearch}
                onChange={(e) => setUseDeepResearch(e.target.checked)}
                disabled={isSubmitting}
                slotProps={{
                  // Switch hard-codes slotProps.input = { role: 'switch' } and spreads it
                  // AFTER the deprecated `inputProps` prop, so `inputProps` alone is
                  // silently dropped here; `slotProps.input` is the only path that reaches
                  // the actual <input>. Keep `role: 'switch'` so we don't regress a11y.
                  input: {
                    role: 'switch',
                    'data-testid': AUDIENCE_GENERATION.deepResearchSwitch,
                  } as React.InputHTMLAttributes<HTMLInputElement>,
                }}
              />
            }
            label="Enable Deep Research (Gemini)"
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', ml: 6 }}
            data-testid={AUDIENCE_GENERATION.deepResearchCaption}
          >
            Uses Google Deep Research for thorough multi-step web analysis. Takes longer (usually ~10 min, up to ~20) but produces more nuanced, data-backed audience segments.
          </Typography>
        </Box>

        {/* Tasks Selection Table */}
        <TableContainer
          component={Paper}
          sx={{
            maxHeight: 400,
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.shape.borderRadius,
            // Matching TasksTable styles
            '& .MuiTableHead-root': {
              '& .MuiTableCell-root': {
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.primary,
                fontWeight: theme.typography.fontWeightMedium,
                borderBottom: `2px solid ${theme.palette.divider}`,
              },
            },
            '& .MuiTableBody-root': {
              '& .MuiTableRow-root': {
                backgroundColor: theme.palette.background.paper,
                '&:hover': {
                  backgroundColor: theme.palette.action.selected,
                },
                '& .MuiTableCell-root': {
                  color: theme.palette.text.primary,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
              },
            },
          }}
        >
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={tasks.length > 0 && selectedTaskIds.length === tasks.length}
                    indeterminate={selectedTaskIds.length > 0 && selectedTaskIds.length < tasks.length}
                    onChange={handleSelectAll}
                    disabled={isSubmitting}
                    slotProps={{
                      // Checkbox is SwitchBase-derived like Switch: `inputProps` alone is
                      // silently dropped, `slotProps.input` is the path to the native <input>.
                      input: {
                        'data-testid': AUDIENCE_GENERATION.selectAllCheckbox,
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                  />
                </TableCell>
                <TableCell>Task Name</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((task) => {
                const taskId = task.id || '';
                const isSelected = selectedTaskIds.includes(taskId);
                return (
                  <TableRow
                    key={taskId}
                    hover
                    selected={isSelected}
                    onClick={() => !isSubmitting && handleToggleTask(taskId)}
                    sx={{ cursor: isSubmitting ? 'default' : 'pointer' }}
                    data-testid={AUDIENCE_GENERATION.taskRow(taskId)}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        disabled={isSubmitting}
                      />
                    </TableCell>
                    <TableCell>{task.name || 'Unnamed Task'}</TableCell>
                    <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {task.description || '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Selection summary */}
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid={AUDIENCE_GENERATION.selectionSummary}
        >
          {selectedTaskIds.length} of {tasks.length} tasks selected
        </Typography>
      </Stack>
    </SDialog>
  );
};
